import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useBot } from "@/context/BotContext";
import Layout from "@/components/Layout";
import {
  TrendingUp, TrendingDown, Target, BarChart3,
} from "lucide-react";

const API = "/api";

const Analytics = () => {
  const { token } = useAuth();
  const { selectedBotId, mode } = useBot();
  const [stats, setStats] = useState(null);
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchStats = useCallback(async () => {
    try {
      const params = selectedBotId ? { bot_id: selectedBotId } : { mode };
      const r = await axios.get(`${API}/analytics`, { params, headers });
      setStats(r.data);
    } catch {}
  }, [selectedBotId, mode]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  if (!stats) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <div style={{ color: 'var(--accent-teal)' }} className="text-sm animate-pulse">Loading analytics...</div>
        </div>
      </Layout>
    );
  }

  const s = stats;

  return (
    <Layout>
      <div className="space-y-5">
        <h1 className="text-[21px] font-bold tracking-tight" style={{ color: 'var(--text-primary)' }}>Analytics</h1>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="card-base p-4 stat-teal">
            <div className="label-style">Total P/L</div>
            <div className="stat-number" style={{ color: (s.total_pnl || 0) >= 0 ? 'var(--accent-teal)' : 'var(--accent-red)' }}>
              {(s.total_pnl || 0) >= 0 ? "+" : ""}${(s.total_pnl || 0).toFixed(2)}
            </div>
          </div>
          <div className="card-base p-4 stat-cyan">
            <div className="label-style">Win Rate</div>
            <div className="stat-number" style={{ color: 'var(--text-primary)' }}>
              {(s.win_rate || 0).toFixed(1)}%
            </div>
            <div className="text-[10px] mt-1" style={{ color: 'var(--text-secondary)' }}>
              <span style={{ color: 'var(--accent-teal)' }}>{s.wins || 0}W</span>{" / "}
              <span style={{ color: 'var(--accent-red)' }}>{s.losses || 0}L</span>
            </div>
          </div>
          <div className="card-base p-4 stat-cyan">
            <div className="label-style">Total Trades</div>
            <div className="stat-number" style={{ color: 'var(--text-primary)' }}>{s.total_trades || 0}</div>
          </div>
          <div className="card-base p-4 stat-yellow">
            <div className="label-style">Avg P/L</div>
            <div className="stat-number" style={{ color: (s.avg_pnl || 0) >= 0 ? 'var(--accent-teal)' : 'var(--accent-red)' }}>
              {(s.avg_pnl || 0) >= 0 ? "+" : ""}${(s.avg_pnl || 0).toFixed(2)}
            </div>
          </div>
        </div>

        {/* Streak and direction */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="card-base">
            <div className="card-header">
              <span className="label-style mb-0">Streaks</span>
            </div>
            <div className="card-content space-y-3">
              <div className="flex justify-between text-sm">
                <span style={{ color: 'var(--text-secondary)' }}>Current Streak</span>
                <span className="font-bold" style={{ color: (s.current_streak || 0) >= 0 ? 'var(--accent-teal)' : 'var(--accent-red)' }}>
                  {(s.current_streak || 0) > 0 ? "+" : ""}{s.current_streak || 0}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span style={{ color: 'var(--text-secondary)' }}>Best Win Streak</span>
                <span className="font-bold" style={{ color: 'var(--accent-teal)' }}>{s.best_win_streak || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span style={{ color: 'var(--text-secondary)' }}>Worst Loss Streak</span>
                <span className="font-bold" style={{ color: 'var(--accent-red)' }}>{s.worst_loss_streak || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span style={{ color: 'var(--text-secondary)' }}>Ladder Failures</span>
                <span className="font-bold" style={{ color: 'var(--accent-yellow)' }}>{s.ladder_failures || 0}</span>
              </div>
            </div>
          </div>

          <div className="card-base">
            <div className="card-header">
              <span className="label-style mb-0">Direction</span>
            </div>
            <div className="card-content space-y-3">
              <div className="flex justify-between text-sm">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" style={{ color: 'var(--accent-teal)' }} />
                  <span style={{ color: 'var(--text-secondary)' }}>UP Trades</span>
                </div>
                <span style={{ color: 'var(--text-primary)' }}>{s.up_trades || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <div className="flex items-center gap-2">
                  <TrendingDown className="w-4 h-4" style={{ color: 'var(--accent-red)' }} />
                  <span style={{ color: 'var(--text-secondary)' }}>DOWN Trades</span>
                </div>
                <span style={{ color: 'var(--text-primary)' }}>{s.down_trades || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span style={{ color: 'var(--text-secondary)' }}>UP Win Rate</span>
                <span className="font-bold" style={{ color: 'var(--accent-teal)' }}>{(s.up_win_rate || 0).toFixed(1)}%</span>
              </div>
              <div className="flex justify-between text-sm">
                <span style={{ color: 'var(--text-secondary)' }}>DOWN Win Rate</span>
                <span className="font-bold" style={{ color: 'var(--accent-cyan)' }}>{(s.down_win_rate || 0).toFixed(1)}%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Daily breakdown */}
        {s.daily_breakdown?.length > 0 && (
          <div className="card-base">
            <div className="card-header">
              <span className="label-style mb-0">Daily Breakdown</span>
            </div>
            <div>
              <div className="table-header grid grid-cols-5 gap-2 px-4 py-3">
                {["Date", "Trades", "Wins", "Losses", "P/L"].map(h => (
                  <div key={h}>{h}</div>
                ))}
              </div>
              {s.daily_breakdown.slice(0, 14).map((d, i) => (
                <div key={d.date || i} className="grid grid-cols-5 gap-2 px-4 py-2.5 table-row text-[13px]">
                  <div style={{ color: 'var(--text-secondary)' }}>{d.date}</div>
                  <div style={{ color: 'var(--text-primary)' }}>{d.trades}</div>
                  <div style={{ color: 'var(--accent-teal)' }}>{d.wins}</div>
                  <div style={{ color: 'var(--accent-red)' }}>{d.losses}</div>
                  <div className="font-bold" style={{ color: (d.pnl || 0) >= 0 ? 'var(--accent-teal)' : 'var(--accent-red)' }}>
                    {(d.pnl || 0) >= 0 ? "+" : ""}${(d.pnl || 0).toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Analytics;
